import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { postMerchantOverview } from 'Services';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import Modal from 'Components/MerchantOverviewModal/AddModal';
import { RQ_MERCHANT_OVERRIDES, RQ_SINGLE_MERCHANT } from 'Utils';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import styles from './AddMerchantModal.css';

type Props = Pick<React.ComponentProps<typeof Modal>, 'isOpen' | 'closeModal'>;

const AddMerchantModal = ({ isOpen, closeModal }: Props) => {
    const { templateRepositoryId } = useTemplateStore.getState().template;
    const { data: gData } = useGlobalRules({});
    const { addModal } = useReduxModal();
    const globalThreshold = gData?.config?.parameters?.overallThreshold;
    const { path } = useRouteMatch();
    const history = useHistory();
    const { t } = useTranslation();
    const { addTechnicalErrorModal } = useReduxModal();
    const queryClient = useQueryClient();

    const mutation = useMutation([RQ_SINGLE_MERCHANT], postMerchantOverview, {
        onSuccess: (_data, variables) => {
            const { merchantId } = variables;
            history.push(`${path}/merchant/${merchantId}`);
            const content = (
                <div className={styles.successBody} data-test-id='add-merchant-success-msg'>
                    {t('addMerchantSuccessMsg', { merchantId })}
                </div>
            );
            addModal({
                id: 'add-single-merchant-success',
                status: 'success',
                title: t('Merchant added'),
                cancelText: t('Close'),
                content
            });
            queryClient.invalidateQueries([RQ_MERCHANT_OVERRIDES]);
        },
        onError: (err: any) => {
            if (err?.status === 409) {
                addTechnicalErrorModal(t('merchantExistsAlready'), t('unableToAddMerchant'));
            } else {
                addTechnicalErrorModal(t('failedToAddMerchant'), t('unableToAddMerchant'));
            }
        }
    });
    const { isLoading } = mutation;

    const handleSubmit = async ({ id, merchantName, merchantDescription, overallThreshold }: MerchantFormState) => {
        mutation.mutate({
            templateRepositoryId,
            merchantId: id,
            merchantName,
            merchantDescription,
            overallThreshold: overallThreshold ? _.toNumber(overallThreshold) : undefined
        });
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Subjects - Add Subject',
                text: 'Add Subject'
            }
        });
    };

    return isOpen ? (
        <Modal
            isOpen={isOpen}
            closeModal={closeModal}
            globalThreshold={Number(globalThreshold)}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
        />
    ) : null;
};

export default AddMerchantModal;
